/**
 * Orchestrates a chat turn: optimistic user message (with any uploaded
 * attachments) → SSE stream of the assistant reply (via chat-stream) → persist
 * the conversation (via the conversations mutations). Wires the SSE deltas into
 * the Zustand session store, and surfaces rag_chunks for the current turn.
 *
 * Three entry points share the same streaming core (`runTurn`):
 *   - send       — append the user turn, then stream a reply.
 *   - regenerate — drop the last assistant reply, re-stream from the prior user
 *                  turn (no new user message appended).
 *   - cancel     — abort the in-flight stream; the partial reply is finalised
 *                  and persisted (a user Stop is not an error).
 *
 * Cancellation + concurrency (per the 2026-06-20 codex review of this wiring):
 *   - The active AbortController lives in the SHARED `chat-run` registry, not a
 *     hook-local ref, so a Stop from ANY surface aborts the one real stream.
 *   - Each turn captures a `runId`; its teardown (`finally`) only mutates shared
 *     streaming state if it is still the active run, so an older aborted turn
 *     can't clobber a newer one or persist a stale draft.
 *   - A user-initiated abort is detected via `signal.aborted` (primary) and
 *     `AbortError` (fallback); it is NOT an error — the partial is finalised.
 *   - An SSE `error` frame is a terminal FAILURE: the turn is not persisted.
 *
 * Kept as a data-access hook so the ChatPage components stay declarative.
 */
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useSessionStore } from '@/features/chat/store/sessionStore';
import type { SessionMessage } from '@/features/chat/types';
import { cancelActiveRun, endRun, isActiveRun, startRun } from './chat-run';
import { streamChat } from './chat-stream';
import { deriveConversationTitle } from './conversation-title';
import {
  useCreateConversation,
  useUpdateConversation,
} from './useConversations';

export function useChat() {
  // Selector-based subscriptions (NOT a bare `useSessionStore()`): this hook is
  // consumed by MessageThread/Composer/MessageActions/the keyboard hook, so a
  // whole-store subscription would re-render all of them on EVERY unrelated
  // store write — most painfully the per-tick artifact-panel width updates during
  // a resize drag. Subscribing field-by-field scopes re-renders to actual chat
  // state changes. (Per the 2026-06-20 codex review, finding #2.) Action
  // identities are stable across renders, so selecting them is free.
  const activeConversationId = useSessionStore((s) => s.activeConversationId);
  const draftMessages = useSessionStore((s) => s.draftMessages);
  const isStreaming = useSessionStore((s) => s.isStreaming);
  const pendingAttachments = useSessionStore((s) => s.pendingAttachments);
  const adoptConversationId = useSessionStore((s) => s.adoptConversationId);
  const appendMessage = useSessionStore((s) => s.appendMessage);
  const appendToLastAssistant = useSessionStore((s) => s.appendToLastAssistant);
  const dropLastAssistant = useSessionStore((s) => s.dropLastAssistant);
  const dropTrailingEmptyAssistant = useSessionStore(
    (s) => s.dropTrailingEmptyAssistant,
  );
  const setStreaming = useSessionStore((s) => s.setStreaming);
  const setRagChunks = useSessionStore((s) => s.setRagChunks);
  const setLastAssistantCitations = useSessionStore(
    (s) => s.setLastAssistantCitations,
  );
  const clearPendingAttachments = useSessionStore(
    (s) => s.clearPendingAttachments,
  );

  const createConversation = useCreateConversation();
  const updateConversation = useUpdateConversation();

  /**
   * The shared streaming core. Assumes the caller has already appended the
   * empty assistant placeholder it streams into, set isStreaming, and cleared
   * rag chunks. Streams the SSE reply, then persists the conversation. Handles
   * a user-initiated abort cleanly: the partial reply (already in the store) is
   * finalised and persisted, and NO error toast is shown. An SSE error frame is
   * terminal — the turn is not persisted.
   *
   * @param titleSeed text used to derive the conversation title on first save.
   */
  const runTurn = useCallback(
    async (titleSeed: string) => {
      const { controller, runId } = startRun();
      // The draft this turn streams into. If the user switches conversation
      // (open / new / delete-active) mid-turn, the turn is ABANDONED at once:
      // the stream is aborted, isStreaming is released (even while a save is
      // pending — mutations do not take this signal), late frames are dropped
      // and nothing is saved. Otherwise they would land in, and be saved with,
      // the conversation the user moved to.
      const epoch = useSessionStore.getState().draftEpoch;
      const ownsDraft = () => useSessionStore.getState().draftEpoch === epoch;
      let unsubscribed = false;
      const unsubscribe = useSessionStore.subscribe((s) => {
        if (s.draftEpoch !== epoch) {
          controller.abort();
          finishTeardown();
        }
      });

      // Tracks whether the user pressed Stop. A user-initiated abort is NOT an
      // error: we suppress the error toast AND still persist whatever partial
      // assistant text streamed in before the cancel (the partial is already in
      // draftMessages — appendToLastAssistant mutated it in place). So Stop
      // finalises the turn cleanly rather than discarding it.
      let aborted = false;
      // A terminal SSE error frame: surfaced to the user, and the turn is NOT
      // persisted (it is a broken reply, not a real answer).
      let streamErrored = false;

      // History excludes the empty assistant placeholder. Attachments ride
      // along on the user turns exactly as send.js expects.
      const history = [...useSessionStore.getState().draftMessages].filter(
        (m) => !(m.role === 'assistant' && m.content === ''),
      );

      function finishTeardown() {
        if (!unsubscribed) {
          unsubscribed = true;
          unsubscribe();
        }
        // Only the still-active run owns shared streaming state. An older turn
        // unwinding after a newer one started must NOT flip isStreaming or the
        // registry out from under the newer turn.
        if (isActiveRun(runId)) {
          setStreaming(false);
          endRun(runId);
        }
      }

      try {
        await streamChat(
          { messages: history, conversation_id: activeConversationId },
          (event) => {
            if (!ownsDraft()) {
              controller.abort();
              return;
            }
            if (event.kind === 'content') {
              appendToLastAssistant(event.content);
            } else if (event.kind === 'rag_chunks') {
              setRagChunks(event.chunks);
            } else if (event.kind === 'citations') {
              // Final metadata frame (before [DONE]): attach the structured
              // inline source citations to the assistant turn being streamed.
              setLastAssistantCitations(
                event.citations,
                event.hasUnsourcedClaim,
              );
            } else if (event.kind === 'error') {
              streamErrored = true;
              toast.error(event.message);
            }
          },
          controller.signal,
        );
      } catch (err) {
        // Robust abort detection: the signal is the source of truth; AbortError
        // (a DOMException) is the fallback for environments that surface it that
        // way.
        const isAbort =
          controller.signal.aborted ||
          (err instanceof DOMException && err.name === 'AbortError') ||
          (err as { name?: unknown })?.name === 'AbortError';
        if (isAbort) {
          aborted = true; // user-initiated — fall through to persist the partial
        } else {
          // A genuine transport failure: surface it and do NOT persist.
          toast.error('Something went wrong. Please try again.');
          finishTeardown();
          return;
        }
      }

      // The user moved to another conversation mid-turn: abandon this turn.
      // Touch nothing in the (now different) draft and save nothing.
      if (!ownsDraft()) {
        finishTeardown();
        return;
      }

      // A terminal SSE error frame is a broken turn — don't persist it. (The
      // user already saw the toast.) Drop the empty assistant placeholder so the
      // thread doesn't keep a "…" stub.
      if (streamErrored) {
        dropTrailingEmptyAssistant();
        finishTeardown();
        return;
      }

      // If the user stopped before ANY token arrived, the assistant placeholder
      // is still empty — drop it rather than persist a blank "…" bubble.
      if (aborted) {
        dropTrailingEmptyAssistant();
      }

      // Reaching here means the stream completed normally or was stopped by the
      // user with at least some content. Persist the conversation.
      try {
        const finalMessages = useSessionStore.getState().draftMessages;
        // Never shortened client-side: the server scrubs PII out of the title
        // (whole values only), then truncates (see conversation-title.ts).
        const title = deriveConversationTitle(titleSeed);
        if (activeConversationId) {
          await updateConversation.mutateAsync({
            id: activeConversationId,
            body: { messages: finalMessages, title },
          });
        } else {
          const created = await createConversation.mutateAsync({
            title,
            messages: finalMessages,
          });
          // Only adopt the new id if the user is still on this draft — a late
          // create must not hijack a conversation they started meanwhile.
          // adoptConversationId is not a navigation (no epoch bump).
          if (ownsDraft()) adoptConversationId(created.id);
        }
      } catch {
        // Persistence failed after a successful/stopped stream. The reply is
        // already on screen; a save failure is non-fatal for a stopped turn but
        // worth a toast for a completed one. (Every mutation failure is also
        // toasted by the app-wide MutationCache in app/providers.tsx.)
        if (!aborted) {
          toast.error('Something went wrong. Please try again.');
        }
      } finally {
        finishTeardown();
      }
    },
    [
      activeConversationId,
      appendToLastAssistant,
      dropTrailingEmptyAssistant,
      setStreaming,
      setRagChunks,
      setLastAssistantCitations,
      adoptConversationId,
      createConversation,
      updateConversation,
    ],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      const ready = pendingAttachments.filter((a) => !a.uploading);
      const hasAttachments = ready.length > 0;
      if ((!trimmed && !hasAttachments) || isStreaming) return;
      if (pendingAttachments.some((a) => a.uploading)) {
        toast.error('Please wait for file uploads to complete.');
        return;
      }

      const userMessage: SessionMessage = {
        role: 'user',
        content: trimmed,
        ...(hasAttachments
          ? {
              attachments: ready.map((a) => ({
                name: a.name,
                type: a.type,
                size: a.size,
                path: a.path,
                url: a.url,
              })),
            }
          : {}),
      };
      appendMessage(userMessage);
      // Seed an empty assistant message we stream into.
      appendMessage({ role: 'assistant', content: '' });
      setStreaming(true);
      setRagChunks([]);
      clearPendingAttachments();

      await runTurn(trimmed || ready[0]?.name || 'New conversation');
    },
    [
      isStreaming,
      pendingAttachments,
      appendMessage,
      setStreaming,
      setRagChunks,
      clearPendingAttachments,
      runTurn,
    ],
  );

  /**
   * Re-run the LAST assistant turn: drop the previous reply and re-stream a
   * fresh one from the same prior user message. No new user turn is appended —
   * the model just gets another pass at the same question. No-op while
   * streaming or when there is no completed assistant reply to replace.
   */
  const regenerate = useCallback(async () => {
    if (isStreaming) return;
    const msgs = useSessionStore.getState().draftMessages;
    const last = msgs[msgs.length - 1];
    // Only regenerate when the conversation actually ends on an assistant reply
    // that followed a user turn.
    if (!last || last.role !== 'assistant') return;
    const priorUser = msgs[msgs.length - 2];
    if (!priorUser || priorUser.role !== 'user') return;

    // Drop the old reply, seed a fresh empty assistant to stream into.
    dropLastAssistant();
    appendMessage({ role: 'assistant', content: '' });
    setStreaming(true);
    setRagChunks([]);

    await runTurn(priorUser.content || 'New conversation');
  }, [
    isStreaming,
    dropLastAssistant,
    appendMessage,
    setStreaming,
    setRagChunks,
    runTurn,
  ]);

  /**
   * Stop the in-flight stream. Aborts the SHARED controller so the abort lands
   * on the real fetch regardless of which surface called this. Teardown of
   * isStreaming is intentionally LEFT to the owning turn's finally block — see
   * the codex-reviewed race note in the file docstring — so a Stop can't
   * re-enable send/regenerate before the aborted turn has finalised + persisted.
   */
  const cancel = useCallback(() => {
    cancelActiveRun();
  }, []);

  return { messages: draftMessages, isStreaming, send, regenerate, cancel };
}
